import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Bot, BookOpen, Check, Copy, Lightbulb, RotateCcw,
  ShieldCheck, ThumbsDown, ThumbsUp, User,
} from 'lucide-react';
import { Badge } from '../ui/Badge.jsx';
import { Button } from '../ui/Button.jsx';
import { IconButton } from '../ui/IconButton.jsx';
import { Alert } from '../ui/Alert.jsx';
import { Link } from '../ui/Link.jsx';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import EtchAction from '../etch/EtchAction.jsx';
import MessageMarkdown from './MessageMarkdown.jsx';
import { authorLabel, isAiAuthored, resolveAuthor } from './authorship';
import { appendAiCopyNotice } from './copy';
import { TRUST_LABEL_KEY, formatSourceReference, hasUnreachableSource, resolveSources, resolveTrust } from './trust';
import { NOTICE_I18N, knowledgeNotices, noticeSettingsPath } from './notices';

const TRUST_ICON = {
  grounded: ShieldCheck,
  inference: Lightbulb,
  model: Bot,
};

/**
 * One turn of the chat thread (design spec §9.1, §10.2).
 *
 * Role is expressed on `data-role`, never on a modifier class (§6.7). Trust is
 * always stated in words next to an icon — colour alone would violate §12, and
 * "which part of this is company knowledge?" is the question this whole surface
 * exists to answer.
 *
 * @param {object} props
 * @param {object} props.message
 * @param {boolean} [props.streaming] - this message is the in-flight answer.
 * @param {boolean} [props.isLast]
 * @param {object[]} [props.sources] - resolved descriptors (streaming case).
 * @param {string|null} [props.activeSourceId]
 * @param {(id: string) => void} [props.onOpenSource]
 * @param {(question: string) => void} [props.onSuggestionClick]
 * @param {(id: string|number, rating: 'up'|'down') => void} [props.onRate]
 * @param {() => void} [props.onRetry]
 */
export function Message({
  message,
  streaming = false,
  isLast = false,
  sources: streamingSources,
  activeSourceId = null,
  onOpenSource,
  onSuggestionClick,
  onRate,
  onRetry,
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const { role, content, suggestions, timestamp, axiom, queries, feedback } = message;
  const isUser = role === 'user';

  // Attribution is a property of the turn, not of whoever is looking at it:
  // a shared thread must read the same for every viewer, and switching persona
  // must not rewrite who answered three questions ago. See ./authorship.js.
  const author = useMemo(() => resolveAuthor(message, user), [message, user]);
  const label = authorLabel(author, t);

  const resolved = useMemo(
    () => resolveSources({ content, sources: streamingSources ?? message.sources ?? [] }),
    [content, streamingSources, message.sources]
  );
  const sourcesById = useMemo(() => new Map(resolved.map((s) => [s.id, s])), [resolved]);
  const trust = useMemo(
    () => resolveTrust({ content, sources: resolved, trust: message.trust }),
    [content, resolved, message.trust]
  );
  const unreachable = hasUnreachableSource(resolved);
  const notices = useMemo(() => knowledgeNotices(message), [message]);
  const TrustIcon = TRUST_ICON[trust] || Bot;

  // The panel needs this message's own descriptors, not just the id — an older
  // turn's citation must open that turn's sources, not the newest answer's.
  const openSource = (id) => onOpenSource?.(id, resolved);

  const state = message.isError
    ? 'error'
    : message.cancelled
      ? 'cancelled'
      : message.partial
        ? 'partial'
        : streaming
          ? 'streaming'
          : 'complete';

  const handleCopy = async () => {
    try {
      const stamp = timestamp
        ? new Date(timestamp).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
        : '';
      const header = `**${label}**${stamp ? ` — ${stamp}` : ''}\n\n`;
      const cited = resolved.filter((s) => s.cited);
      const footer = cited.length
        ? `\n\n---\n\n**${t('chat.sources')}**\n${cited.map((s) => `- [S${s.index}] ${formatSourceReference(s) || t('chat.sourceUnreachable')}`).join('\n')}\n`
        : '';
      // Art. 50 Abs. 2 AI Act: the marking has to survive the copy — a badge
      // in the DOM cannot follow the text into a document or an email. See
      // ./copy.js.
      const payload = appendAiCopyNotice(`${header}${content}${footer}`, isAiAuthored(author), t('chat.aiCopyNotice'));
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      toast.error(err.message || t('states.error'));
    }
  };

  const citedCount = resolved.filter((s) => s.cited).length;

  return (
    <article className="etch-message" data-role={role} data-state={state}>
      <span className="etch-message__avatar" aria-hidden="true">
        {isUser ? <User size={16} /> : <Bot size={16} />}
      </span>

      <div className="etch-message__main">
        <header className="etch-message__header">
          <span className="etch-message__author" data-author={author.kind}>{label}</span>
          {isAiAuthored(author) && (
            <span className="etch-message__ai" title={t('chat.aiBadgeLabel')}>
              <span aria-hidden="true">{t('chat.aiBadge')}</span>
              <span className="etch-visually-hidden">{t('chat.aiBadgeLabel')}</span>
            </span>
          )}
          {timestamp && (
            <time className="etch-message__time" dateTime={new Date(timestamp).toISOString()}>
              {new Date(timestamp).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
            </time>
          )}
          {!isUser && !streaming && (
            <span className="etch-message__trust" data-trust={trust}>
              <TrustIcon size={13} aria-hidden="true" />
              <span>{t(TRUST_LABEL_KEY[trust])}</span>
            </span>
          )}
          {!isUser && unreachable && (
            <Badge tone="warning">
              <AlertTriangle size={12} aria-hidden="true" /> {t('chat.sourceUnreachable')}
            </Badge>
          )}
          {streaming && (
            <span className="etch-message__streaming" data-busy="true">
              <span className="etch-message__dots" aria-hidden="true">
                <span /><span /><span />
              </span>
              <span className="etch-visually-hidden">{t('chat.streaming')}</span>
            </span>
          )}
        </header>

        <div
          className="etch-message__body"
          aria-busy={streaming || undefined}
          data-ai-generated={isAiAuthored(author) ? 'true' : undefined}
        >
          {isUser ? (
            <p className="etch-message__text">{content}</p>
          ) : content ? (
            <MessageMarkdown
              content={content}
              sourcesById={sourcesById}
              activeSourceId={activeSourceId}
              onOpenSource={openSource}
              streaming={streaming}
            />
          ) : streaming ? (
            <p className="etch-message__thinking">{t('chat.thinking')}</p>
          ) : null}
        </div>

        {message.isError && (
          <Alert tone="danger" title={t('states.error')}>
            {message.errorMessage || t('states.error')}
          </Alert>
        )}
        {message.cancelled && (
          <p className="etch-message__note" data-note="cancelled">{t('chat.cancelled')}</p>
        )}
        {message.partial && (
          <p className="etch-message__note" data-note="partial">{t('chat.partial')}</p>
        )}

        {/* Why an answer rests on model knowledge although a knowledge base is
            connected — e.g. an Axiom token with several targets and no scope. */}
        {!isUser && !streaming && notices.map((code) => (
          <Alert key={code} tone="warning" title={t(NOTICE_I18N[code].title)}>
            {t(NOTICE_I18N[code].body)}{' '}
            <Link as={RouterLink} to={noticeSettingsPath(code, user)}>{t(NOTICE_I18N[code].action)}</Link>
          </Alert>
        ))}

        {/* Legacy persona/Axiom diagnostics — this path emits no [S1] markers,
            so it is rendered as a plain document list, never as citations. */}
        {!isUser && axiom?.documents?.length > 0 && (
          <p className="etch-message__legacy-sources">
            <BookOpen size={12} aria-hidden="true" />
            <span>{t('chat.sourceDocumentCount', { count: axiom.documents.length })}</span>
            <span className="etch-message__legacy-list">{axiom.documents.join(', ')}</span>
          </p>
        )}

        {!isUser && queries?.length > 0 && (
          <p className="etch-message__queries">
            <span className="etch-message__queries-label">{t('chat.queried')}</span>
            {queries.map((query, index) => (
              <Badge key={`${query.type}-${index}`}>{query.type}</Badge>
            ))}
          </p>
        )}

        {!isUser && !streaming && (
          <div className="etch-message__actions" role="group" aria-label={t('chat.messageActions')}>
            {resolved.length > 0 && (
              <Button
                variant="ghost"
                size="compact"
                data-action="sources"
                onClick={() => openSource(resolved.find((s) => s.cited)?.id || resolved[0].id)}
              >
                <BookOpen size={13} aria-hidden="true" />
                <span>{t('chat.sourcesWithCount', { count: citedCount || resolved.length })}</span>
              </Button>
            )}
            <Button variant="ghost" size="compact" data-action="copy" onClick={handleCopy}>
              {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
              <span>{copied ? t('chat.copied') : t('chat.copy')}</span>
            </Button>
            <IconButton
              label={t('chat.feedbackUp')}
              pressed={feedback === 'up'}
              data-action="feedback-up"
              onClick={() => onRate?.(message.id, 'up')}
            >
              <ThumbsUp size={14} />
            </IconButton>
            <IconButton
              label={t('chat.feedbackDown')}
              pressed={feedback === 'down'}
              data-action="feedback-down"
              onClick={() => onRate?.(message.id, 'down')}
            >
              <ThumbsDown size={14} />
            </IconButton>
            {isLast && onRetry && (
              <Button variant="ghost" size="compact" data-action="retry" onClick={onRetry}>
                <RotateCcw size={13} aria-hidden="true" />
                <span>{t('chat.retry')}</span>
              </Button>
            )}
            {content && (
              <EtchAction
                text={content}
                sources={resolved}
                defaultSourceId={resolved.find((s) => s.cited && s.state !== 'unreachable')?.id || null}
              />
            )}
          </div>
        )}

        {!isUser && !streaming && suggestions?.length > 0 && (
          <ul className="etch-message__suggestions">
            {suggestions.map((suggestion, index) => (
              <li key={index}>
                <Button variant="secondary" size="compact" onClick={() => onSuggestionClick?.(suggestion)}>
                  {suggestion}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

export default Message;
